import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ActivityIndicator,
  Image,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Crypto from 'expo-crypto';
import { analyzeImage, AnalyzedItem } from '../lib/gemini';
import { getUserProductById, updateUserProduct } from '../utils/userProductStorage';
import { UserProductWithDetails } from '../types/newDatabaseTypes';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../styles/ThemeProvider';

interface Props {
  navigation: any;
  route: {
    params: {
      itemId: string;
      imageUrl: string;
      currentItem: any;
    };
  };
}

interface EditableAnalysis {
  name: string;
  brand: string;
  model: string;
  description: string;
  confidence: number;
  condition: string;
  // V3 P7: provenance of `condition` (the field is read-only here).
  conditionSource: 'model_inferred' | 'client_default' | null;
  category: string;
}

export default function ReanalyzeItemScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        headerTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        saveButton: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        saveButtonDisabled: {
          color: theme.colors.textTertiary,
        },
        content: {
          flex: 1,
          padding: theme.spacing.lg,
        },
        image: {
          width: '100%',
          height: 200,
          borderRadius: theme.radius.radiusMd,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          marginBottom: theme.spacing.lg,
        },
        confidenceContainer: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          marginBottom: theme.spacing.lg,
        },
        confidenceLabel: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        confidenceValue: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        section: {
          marginBottom: theme.spacing.xl,
        },
        sectionTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.md,
        },
        field: {
          marginBottom: theme.spacing.lg,
        },
        fieldLabel: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.xs,
        },
        textInput: {
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          ...theme.typography.md,
          color: theme.colors.textPrimary,
          backgroundColor: theme.colors.backgroundSurface,
        },
        multilineInput: {
          height: 80,
          textAlignVertical: 'top',
        },
        readOnlyInput: {
          backgroundColor: theme.colors.backgroundElevated,
          color: theme.colors.textSecondary,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        loadingText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.md,
          textAlign: 'center',
        },
        errorText: {
          ...theme.typography.md,
          color: theme.colors.destructive,
          textAlign: 'center',
          marginBottom: theme.spacing.xl,
        },
        retryButton: {
          backgroundColor: theme.colors.accent,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.xl,
          borderRadius: theme.radius.radiusMd,
        },
        retryButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.backgroundPrimary,
        },
      }),
    [theme],
  );
  const { itemId, imageUrl, currentItem } = route.params;
  const { user } = useAuth();
  const [analyzing, setAnalyzing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalyzedItem | null>(null);
  const [editableData, setEditableData] = useState<EditableAnalysis | null>(null);
  const [fullItem, setFullItem] = useState<UserProductWithDetails | null>(null);

  useEffect(() => {
    fetchItemAndReanalyze();
  }, []);

  const fetchItemAndReanalyze = async () => {
    try {
      setAnalyzing(true);
      
      // Fetch full item details
      const item = await getUserProductById(itemId);
      if (!item) {
        Alert.alert('Error', 'Item not found');
        navigation.goBack();
        return;
      }
      setFullItem(item);

      // Convert image URL to base64 for Gemini analysis
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove the data URL prefix to get just the base64 data
          const base64Data = base64String.split(',')[1];
          resolve(base64Data);
        };
        reader.readAsDataURL(blob);
      });

      // Re-analyze the image with Gemini
      const analysisResults = await analyzeImage(base64);
      
      if (analysisResults && analysisResults.length > 0) {
        const analysis = analysisResults[0]; // Take the first result
        setAnalysisResult(analysis);
        
        // Set up editable data with new analysis
        setEditableData({
          name: analysis.name || currentItem.modelName || '',
          brand: analysis.brand || currentItem.brandName || '',
          model: analysis.model || currentItem.modelName || '',
          description: analysis.description || '',
          confidence: analysis.confidenceScore || 0,
          condition: analysis.condition || currentItem.condition || 'Good',
          conditionSource: analysis.condition
            ? 'model_inferred'
            : currentItem.condition
              ? null
              : 'client_default',
          category: analysis.category || '',
        });
      } else {
        Alert.alert('Analysis Failed', 'Unable to analyze the image. Please try again.');
        navigation.goBack();
      }
    } catch (error) {
      console.error('Error during reanalysis:', error);
      Alert.alert('Error', 'Failed to analyze the image. Please try again.');
      navigation.goBack();
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    console.log('[DEBUG] handleSave called');
    console.log('[DEBUG] editableData:', editableData);
    console.log('[DEBUG] fullItem:', fullItem);
    console.log('[DEBUG] user:', user);
    
    if (!editableData || !fullItem || !user) {
      console.log('[DEBUG] Missing required data, returning early');
      return;
    }

    setSaving(true);
    try {
      // Update the item with new analysis data
      // V3 P7 (U8): saving a reanalysis is an explicit re-identification request. The
      // backend worker supersedes the item's current identification from its stored image
      // (history kept, one current decision), keyed by this request id so a retry is a
      // no-op. No valuation is written: the item's value belongs to the P8 projector.
      const updates = {
        condition: editableData.condition,
        ...(editableData.condition !== fullItem.condition && editableData.conditionSource
          ? { condition_source: editableData.conditionSource }
          : {}),
        image_analysis: {
          ...((fullItem.image_analysis as any) || {}),
          confidence_score: editableData.confidence,
          description: editableData.description,
          category: editableData.category,
          reidentify_request: { id: Crypto.randomUUID(), requested_at: new Date().toISOString() },
        }
      };

      console.log('[DEBUG] Updates to apply:', updates);
      console.log('[DEBUG] Item ID:', itemId);

      await updateUserProduct(itemId, updates);
      
      console.log('[DEBUG] Update successful, navigating back');
      
      // Navigate back immediately
      navigation.navigate('CacheList', { activeTab: 'needs_review' });
      
      // Show success message after navigation
      setTimeout(() => {
        Alert.alert('Success', 'Item updated successfully!');
      }, 100);
    } catch (error) {
      console.error('[ERROR] Error saving updated item:', error);
      Alert.alert('Error', 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof EditableAnalysis, value: string | number) => {
    if (!editableData) return;
    setEditableData({
      ...editableData,
      [field]: value
    });
  };

  if (analyzing) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color="#000" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Reanalyzing Item</Text>
          <View style={{ width: 24 }} />
        </View>
        
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#000" />
          <Text style={styles.loadingText}>Analyzing image with AI...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!editableData) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color="#000" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Analysis Failed</Text>
          <View style={{ width: 24 }} />
        </View>
        
        <View style={styles.centered}>
          <Text style={styles.errorText}>Unable to analyze the image</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchItemAndReanalyze}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#000" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Review Analysis</Text>
        <TouchableOpacity onPress={() => {
          console.log('[DEBUG] Save button pressed');
          handleSave();
        }} disabled={saving}>
          <Text style={[styles.saveButton, saving && styles.saveButtonDisabled]}>
            {saving ? 'Saving...' : 'Save'}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content}>
        {/* Image */}
        <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="contain" />
        
        {/* Confidence Score */}
        <View style={styles.confidenceContainer}>
          <Text style={styles.confidenceLabel}>New Confidence Score:</Text>
          <Text style={[styles.confidenceValue, { 
            color: editableData.confidence >= 0.9 ? '#10B981' : 
                   editableData.confidence >= 0.3 ? '#F59E0B' : '#EF4444' 
          }]}>
            {Math.round(editableData.confidence * 100)}%
          </Text>
        </View>

        {/* Editable Fields */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Product Information</Text>
          
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Brand</Text>
            <TextInput
              style={[styles.textInput, styles.readOnlyInput]}
              value={editableData.brand}
              editable={false}
              placeholder="Brand name"
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Model</Text>
            <TextInput
              style={[styles.textInput, styles.readOnlyInput]}
              value={editableData.model}
              editable={false}
              placeholder="Model name"
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Description</Text>
            <TextInput
              style={[styles.textInput, styles.multilineInput, styles.readOnlyInput]}
              value={editableData.description}
              editable={false}
              placeholder="Item description"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Category</Text>
            <TextInput
              style={[styles.textInput, styles.readOnlyInput]}
              value={editableData.category}
              editable={false}
              placeholder="Product category"
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Condition</Text>
            <TextInput
              style={[styles.textInput, styles.readOnlyInput]}
              value={editableData.condition}
              editable={false}
              placeholder="Item condition"
            />
          </View>

        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

